import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, AlertCircle, TrendingUp, Activity, Search, ArrowLeft, Calendar, Video, X, ExternalLink, Maximize2, Minimize2, Database, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import Leaderboards from '../components/Leaderboards';
import CoachCalendar from '../components/CoachCalendar';
import PendingPlayers from '../components/PendingPlayers';
import BulkUploadButton from '../components/BulkUploadButton';
import AllAthletesManager from '../components/AllAthletesManager';
import AthleteCard from '../components/dashboard/AthleteCard';
import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function CoachDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const coachName = searchParams.get('coach') || 'John Smith';
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  
  // Calendar & Meet state
  const [showCalendar, setShowCalendar] = useState(true);
  const [meetUrl, setMeetUrl] = useState(null);
  const [meetExpanded, setMeetExpanded] = useState(false);
  
  // All Athletes Manager state
  const [showAllAthletes, setShowAllAthletes] = useState(false);
  
  // Medical statuses for athlete cards
  const [medicalStatuses, setMedicalStatuses] = useState({});
  
  // Child data (measurements) mapped by child ID
  const [childrenData, setChildrenData] = useState({});
  
  // Teams list
  const [teams, setTeams] = useState([]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Reset to page 1 on new search
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString()
      });
      
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      if (teamFilter) {
        params.append('team_filter', teamFilter);
      }
      
      const response = await axios.get(
        `${API}/coach-dashboard/${encodeURIComponent(coachName)}?${params.toString()}`, 
        { headers }
      );
      setDashboardData(response.data);
      
      // Extract child IDs and build measurement data map
      const athletes = response.data?.athletes || [];
      const childIds = athletes.map(a => a.child?.id).filter(Boolean);
      
      // Build childrenData from body_composition
      const dataMap = {};
      const teamsSet = new Set();
      athletes.forEach(athlete => {
        if (athlete.child?.id && athlete.body_composition) {
          // Map body_composition to expected measurement format
          dataMap[athlete.child.id] = {
            standing_height: athlete.body_composition.height_cm,
            weight: athlete.body_composition.weight_kg,
            bmi: athlete.body_composition.bmi,
            bmi_percentile: athlete.body_composition.bmi_percentile,
            uk_who_percentiles: {
              height_percentile: athlete.body_composition.height_percentile,
              weight_percentile: athlete.body_composition.weight_percentile
            }
          };
        }
        // Collect teams
        if (athlete.child?.current_team_name) {
          teamsSet.add(athlete.child.current_team_name);
        }
      });
      setChildrenData(dataMap);
      setTeams(Array.from(teamsSet).sort());
      
      // Batch load medical statuses
      if (childIds.length > 0) {
        try {
          const medicalRes = await axios.post(`${API}/medical/status/batch`, childIds, { headers });
          setMedicalStatuses(medicalRes.data?.statuses || {});
        } catch (e) {
          console.error('Failed to load medical statuses');
          setMedicalStatuses({});
        }
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [coachName, currentPage, pageSize, debouncedSearch, teamFilter]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Client-side risk filter (alerts/mental health) - server handles search and sport
  const getFilteredAthletes = () => {
    if (!dashboardData) return [];
    
    return dashboardData.athletes.filter(athlete => {
      const matchesRisk = 
        riskFilter === 'all' ||
        (riskFilter === 'alerts' && athlete.performance_summary.alerts > 0) ||
        (riskFilter === 'mental_health' && athlete.performance_summary.mental_health_status !== 'low');
      
      return matchesRisk;
    });
  };

  // Reset to page 1 when team filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [teamFilter]);

  if (loading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 flex items-center justify-center">
        <div className="inline-flex items-center gap-3 text-zinc-500">
          <div className="w-6 h-6 border-2 border-[#02b5ff] border-t-transparent rounded-full animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const filteredAthletes = getFilteredAthletes();
  const stats = dashboardData?.dashboard_stats || {};
  const pagination = dashboardData?.pagination || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950" data-testid="coach-dashboard-page">
      {/* Header */}
      <div className="bg-zinc-950/80 backdrop-blur-xl shadow-2xl border-b border-zinc-800/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1" data-testid="coach-dashboard-title">EPP Staff Dashboard</h1>
              <p className="text-sm sm:text-base text-zinc-400">{coachName}</p>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <Button
                onClick={() => setShowAllAthletes(!showAllAthletes)}
                className={`h-10 px-5 font-semibold rounded-full transition-all text-sm ${
                  showAllAthletes 
                    ? 'bg-[#02b5ff] text-white shadow-lg shadow-[#02b5ff]/30' 
                    : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white border border-zinc-700'
                }`}
                data-testid="manage-all-athletes-btn"
              >
                <Users className="mr-2 h-4 w-4" />
                {showAllAthletes ? 'Hide All Athletes' : 'Manage All Athletes'}
              </Button>
              <BulkUploadButton onUploadComplete={loadDashboard} />
              <a
                href="https://app.photonsports.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-10 px-5 bg-gradient-to-r from-[#ff025f] to-[#dd0050] hover:from-[#dd0050] hover:to-[#bb0040] text-white font-semibold rounded-full shadow-lg shadow-[#ff025f]/20 transition-all text-sm"
                data-testid="view-all-data-btn"
              >
                <Database className="h-4 w-4" />
                View All Data
              </a>
              <Button
                data-testid="back-to-main-dashboard"
                onClick={() => navigate('/')}
                variant="outline"
                className="h-10 px-5 border border-zinc-700 hover:border-[#02b5ff]/50 bg-zinc-800/50 text-zinc-300 hover:text-white font-semibold rounded-full transition-all text-sm"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* All Athletes Manager - Show when toggled */}
        {showAllAthletes && (
          <div className="mb-8">
            <AllAthletesManager 
              onAthleteClick={(athleteId) => navigate(`/child/${athleteId}`)}
              onAssignSuccess={loadDashboard}
            />
          </div>
        )}

        {/* Pending Players Section - Show prominently at top */}
        <div className="mb-8">
          <PendingPlayers />
        </div>

        {/* Google Calendar & Meet Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 rounded-xl flex items-center justify-center">
                <Calendar className="h-5 w-5 text-[#02b5ff]" />
              </div>
              <h2 className="text-lg font-bold text-white">Booking Calendar</h2>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCalendar(!showCalendar)}
                className="border-zinc-700 text-zinc-400 hover:text-white hover:border-[#02b5ff]/50 rounded-full"
              >
                {showCalendar ? 'Hide Calendar' : 'Show Calendar'}
              </Button>
              <a
                href="https://calendar.google.com/calendar"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-zinc-700 rounded-full text-zinc-400 hover:text-white hover:border-[#02b5ff]/50 transition-all"
              >
                <ExternalLink className="h-4 w-4" />
                Open in Google
              </a>
            </div>
          </div>
          
          {showCalendar && (
            <CoachCalendar athletes={dashboardData?.athletes?.map(a => a.child) || []} />
          )}
        </div>

        {/* Leaderboards - All ages for coach */}
        <div className="mb-8">
          <Leaderboards isCoach={true} />
        </div>

        {/* Google Meet Embedded Window */}
        {meetUrl && (
          <div className={`fixed ${meetExpanded ? 'inset-4' : 'bottom-4 right-4 w-[500px] h-[400px]'} bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl z-50 overflow-hidden transition-all`} data-testid="meet-window">
            <div className="flex items-center justify-between p-3 bg-zinc-800 border-b border-zinc-700">
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-green-400" />
                <span className="text-sm font-medium text-white">Google Meet</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMeetExpanded(!meetExpanded)}
                  className="p-1.5 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                  title={meetExpanded ? 'Minimize' : 'Maximize'}
                >
                  {meetExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                <a
                  href={meetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                  title="Open in new tab"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
                <button
                  onClick={() => {
                    setMeetUrl(null);
                    setMeetExpanded(false);
                  }}
                  className="p-1.5 hover:bg-red-500/20 rounded text-zinc-400 hover:text-red-400"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <iframe
              src={meetUrl}
              width="100%"
              height={meetExpanded ? 'calc(100% - 48px)' : '352'}
              frameBorder="0"
              allow="camera; microphone; fullscreen; display-capture; autoplay"
              className="bg-black"
              title="Google Meet"
            />
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <User className="h-5 w-5 text-[#02b5ff]" />
              <span className="text-sm font-semibold text-gray-400">Total Athletes</span>
            </div>
            <div className="text-3xl font-bold text-white">{pagination.total_count || stats.total_athletes || 0}</div>
            {loading && <div className="text-xs text-zinc-500 mt-1">Updating...</div>}
          </div>

          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-5 w-5 text-[#02b5ff]" />
              <span className="text-sm font-semibold text-gray-400">Avg Rating</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {stats.average_coach_rating ? `${stats.average_coach_rating}/10` : 'N/A'}
            </div>
          </div>

          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="h-5 w-5 text-[#ff025f]" />
              <span className="text-sm font-semibold text-gray-400">Active Alerts</span>
            </div>
            <div className="text-3xl font-bold text-white">{stats.total_alerts || 0}</div>
          </div>

          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="h-5 w-5 text-[#02b5ff]" />
              <span className="text-sm font-semibold text-gray-400">Sports</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {Object.keys(stats.sports_breakdown || {}).length}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search athletes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-zinc-800 border-zinc-700 rounded-xl"
              />
            </div>

            <Select value={teamFilter || "all"} onValueChange={(val) => setTeamFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="h-12 bg-zinc-800 border-zinc-700 rounded-xl">
                <SelectValue placeholder="Filter by team" />
              </SelectTrigger>
              <SelectContent className="bg-white border-zinc-700 rounded-xl">
                <SelectItem value="all">All Teams</SelectItem>
                {teams.map(team => (
                  <SelectItem key={team} value={team}>{team}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="h-12 bg-zinc-800 border-zinc-700 rounded-xl">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-zinc-700 rounded-xl">
                <SelectItem value="all">All Athletes</SelectItem>
                <SelectItem value="alerts">With Alerts</SelectItem>
                <SelectItem value="mental_health">Mental Health Concerns</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Athletes Grid */}
        {filteredAthletes.length === 0 ? (
          <div className="bg-zinc-900 rounded-2xl p-12 shadow-md text-center">
            <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Athletes Found</h3>
            <p className="text-gray-400">
              {searchTerm || teamFilter 
                ? 'Try adjusting your filters'
                : 'No athletes assigned to this coach yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="epp-athletes-grid">
            {filteredAthletes.map((athlete) => {
              const child = athlete.child;
              return (
                <AthleteCard
                  key={child.id}
                  child={child}
                  latestMeasurement={childrenData[child.id]}
                  medicalStatus={medicalStatuses[child.id]}
                  onDelete={(childId) => {
                    // Refresh the dashboard when an athlete is deleted
                    loadDashboard();
                  }}
                  onUpdate={loadDashboard}
                />
              );
            })}
          </div>
        )}
        
        {/* Pagination Controls - Always show if pagination data exists */}
        {dashboardData?.pagination && dashboardData.pagination.total_pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={!dashboardData.pagination.has_prev || loading}
              className="border-zinc-700 text-zinc-400 hover:text-white disabled:opacity-50"
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            
            <div className="flex items-center gap-2 text-sm">
              <span className="text-zinc-500">Page</span>
              <span className="px-3 py-1 bg-zinc-800 rounded-lg text-white font-medium">
                {dashboardData.pagination.page}
              </span>
              <span className="text-zinc-500">of {dashboardData.pagination.total_pages}</span>
              <span className="text-zinc-600 ml-2">
                ({dashboardData.pagination.total_count} athletes)
              </span>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={!dashboardData.pagination.has_next || loading}
              className="border-zinc-700 text-zinc-400 hover:text-white disabled:opacity-50"
              data-testid="next-page-btn"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
